import { type ReactNode } from 'react';
import { FiCheck } from 'react-icons/fi';
import { useBranding } from '../../hooks/useBranding';

const RECURSOS = [
  'Abra protocolos, pedidos e requerimentos online',
  'Ouvidoria: sugestões, reclamações e denúncias',
  'Acesso à informação (LAI) e Zeladoria',
  'Acompanhe tudo pelo histórico, sem sair de casa',
];

interface AuthShellProps {
  titulo: string;
  subtitulo?: string;
  children: ReactNode;
}

/**
 * Estrutura de 2 colunas das telas de autenticação do portal do cidadão:
 * metade azul institucional com a explicação do sistema + metade branca com o conteúdo.
 */
export function AuthShell({ titulo, subtitulo, children }: AuthShellProps) {
  const branding = useBranding();
  return (
    <div className="min-h-screen flex bg-slate-100">
      {/* Painel institucional */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-institutional via-institutional-dark to-slate-900 text-white p-12 flex-col justify-center">
        <div
          className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '28px 28px' }}
        />
        <div className="relative z-10 max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-16 h-16 rounded-xl bg-white flex items-center justify-center overflow-hidden p-1.5 shadow-sm">
              <img src={branding.logoUrl} alt="" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="text-lg font-bold leading-tight">{branding.nome}</div>
              <div className="text-xs uppercase tracking-widest text-blue-200">{branding.slogan || 'Portal do Cidadão'}</div>
            </div>
          </div>

          <h2 className="text-3xl font-bold leading-tight mb-3">
            A prefeitura mais perto de você
          </h2>
          <p className="text-blue-100/90 text-base leading-relaxed mb-8">
            Faça solicitações, fale com a ouvidoria e acompanhe seus pedidos
            de qualquer lugar, 24 horas por dia.
          </p>
          <ul className="space-y-3">
            {RECURSOS.map((r) => (
              <li key={r} className="flex items-center gap-3 text-sm text-white/90">
                <span className="flex-shrink-0 w-6 h-6 rounded-md bg-white/15 flex items-center justify-center">
                  <FiCheck size={14} className="text-blue-200" />
                </span>
                {r}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 justify-center mb-6">
            <div className="w-11 h-11 rounded-lg bg-white border border-slate-200 flex items-center justify-center overflow-hidden p-1">
              <img src={branding.logoUrl} alt="" className="w-full h-full object-contain" />
            </div>
            <span className="font-bold text-slate-900">{branding.nome}</span>
          </div>
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-slate-900">{titulo}</h1>
            {subtitulo && <p className="text-sm text-slate-500 mt-1">{subtitulo}</p>}
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
